import type { ApplicabilityItem, Channel, MaterialCategory } from "@compliance-sage/contracts";
import {
  groupTypeOfItem,
  isInactive,
  isUnpublishedDraft,
  leafCategories,
  matrixItemKey,
  targetCellKey,
  type ConnectionFilter,
  type SourceGroupType,
} from "../complianceScope/utils";

export const GROUP_ORDER: SourceGroupType[] = ["POLICY", "RULE_CUSTOM", "RULE_TAUGHT", "DICTIONARY"];

const CHANNEL_X = -320;
const CHANNEL_ROW_HEIGHT = 96;
const CATEGORY_X = 0;
// Generous enough to fit a category card with its parent label, name, and up
// to 3 wrapped badge chips without overlapping the next card.
const CATEGORY_ROW_HEIGHT = 132;
const GROUP_X = 460;
const GROUP_ROW_HEIGHT = 140;

export interface ChannelNodeData {
  [key: string]: unknown;
  kind: "channel";
  channelId: string;
  channelName: string;
  /** Which channel the "Connected items" panel edits when a category is clicked. */
  active: boolean;
}

export interface CategoryNodeData {
  [key: string]: unknown;
  kind: "category";
  categoryId: string;
  categoryName: string;
  parentName: string | null;
  selected: boolean;
  /** Aggregated across every channel — how many items connect this category to each group, in at least one channel. */
  counts: Record<SourceGroupType, number>;
  /** True when the active channel has no policy/rule connected to this category. */
  unconnectedForActive: boolean;
  /** Unpublished drafts targeting this category in the active channel. */
  draftCount: number;
}

export interface GroupNodeData {
  [key: string]: unknown;
  kind: "group";
  groupType: SourceGroupType;
  count: number;
  draftCount: number;
}

export interface GraphNode<T> {
  id: string;
  position: { x: number; y: number };
  data: T;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  kind: "channelCategory" | "categoryGroup";
  /** Only set for categoryGroup edges — channelCategory edges use a neutral color instead. */
  groupType?: SourceGroupType;
  /** Forbidden Expressions are unscoped, so their edges are always-on and non-editable. */
  isStatic: boolean;
}

export function channelNodeId(channelId: string): string {
  return `channel:${channelId}`;
}

export function categoryNodeId(categoryId: string): string {
  return `category:${categoryId}`;
}

export function groupNodeId(groupType: SourceGroupType): string {
  return `group:${groupType}`;
}

function parentNameOf(category: MaterialCategory, allCategories: MaterialCategory[]): string | null {
  if (!category.parentId) return null;
  return allCategories.find((candidate) => candidate.id === category.parentId)?.name ?? null;
}

/** Which group types (from the visible items) currently target this channel×category cell. */
function connectedGroupTypes(
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
  channelId: string,
  categoryId: string,
): SourceGroupType[] {
  const key = targetCellKey(channelId, categoryId);
  const types = new Set<SourceGroupType>();
  for (const item of items) {
    if (isInactive(item)) continue;
    if (selections.get(matrixItemKey(item))?.has(key)) {
      types.add(groupTypeOfItem(item));
    }
  }
  return [...types];
}

function draftCountForCell(
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
  channelId: string,
  categoryId: string,
): number {
  const key = targetCellKey(channelId, categoryId);
  let count = 0;
  for (const item of items) {
    if (!isUnpublishedDraft(item)) continue;
    if (selections.get(matrixItemKey(item))?.has(key)) count += 1;
  }
  return count;
}

function draftCountByGroupType(items: ApplicabilityItem[]): Record<SourceGroupType, number> {
  const counts: Record<SourceGroupType, number> = { POLICY: 0, RULE_CUSTOM: 0, RULE_TAUGHT: 0, DICTIONARY: 0 };
  for (const item of items) {
    if (!isUnpublishedDraft(item)) continue;
    counts[groupTypeOfItem(item)] += 1;
  }
  return counts;
}

/** Aggregated across every active channel, deduplicating an item connected through more than one channel. */
function countByGroupTypeAnyChannel(
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
  channels: Channel[],
  categoryId: string,
): Record<SourceGroupType, number> {
  const counts: Record<SourceGroupType, number> = { POLICY: 0, RULE_CUSTOM: 0, RULE_TAUGHT: 0, DICTIONARY: 0 };
  const countedItemKeys = new Set<string>();
  for (const channel of channels) {
    const key = targetCellKey(channel.id, categoryId);
    for (const item of items) {
      if (isInactive(item)) continue;
      const itemKey = matrixItemKey(item);
      if (countedItemKeys.has(itemKey)) continue;
      if (selections.get(itemKey)?.has(key)) {
        counts[groupTypeOfItem(item)] += 1;
        countedItemKeys.add(itemKey);
      }
    }
  }
  return counts;
}

export function buildChannelNodes(channels: Channel[], activeChannelId: string | null): GraphNode<ChannelNodeData>[] {
  return channels
    .filter((channel) => channel.isActive)
    .map((channel, index) => ({
      id: channelNodeId(channel.id),
      position: { x: CHANNEL_X, y: index * CHANNEL_ROW_HEIGHT },
      data: {
        kind: "channel",
        channelId: channel.id,
        channelName: channel.name,
        active: channel.id === activeChannelId,
      },
    }));
}

export function buildCategoryNodes(
  categories: MaterialCategory[],
  allCategories: MaterialCategory[],
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
  channels: Channel[],
  selectedCategoryId: string | null,
  activeChannelId: string | null,
): GraphNode<CategoryNodeData>[] {
  const activeChannels = channels.filter((channel) => channel.isActive);
  return leafCategories(categories).map((category, index) => ({
    id: categoryNodeId(category.id),
    position: { x: CATEGORY_X, y: index * CATEGORY_ROW_HEIGHT },
    data: {
      kind: "category",
      categoryId: category.id,
      categoryName: category.name,
      parentName: parentNameOf(category, allCategories),
      selected: category.id === selectedCategoryId,
      counts: countByGroupTypeAnyChannel(items, selections, activeChannels, category.id),
      unconnectedForActive: activeChannelId
        ? connectedGroupTypes(items, selections, activeChannelId, category.id).length === 0
        : true,
      draftCount: activeChannelId ? draftCountForCell(items, selections, activeChannelId, category.id) : 0,
    },
  }));
}

/** Org-wide active item count per group, independent of channel/category. */
function totalCountByGroupType(items: ApplicabilityItem[]): Record<SourceGroupType, number> {
  const counts: Record<SourceGroupType, number> = { POLICY: 0, RULE_CUSTOM: 0, RULE_TAUGHT: 0, DICTIONARY: 0 };
  for (const item of items) {
    if (isInactive(item)) continue;
    counts[groupTypeOfItem(item)] += 1;
  }
  return counts;
}

export function buildGroupNodes(
  items: ApplicabilityItem[],
  dictionaryEntryCount: number,
): GraphNode<GroupNodeData>[] {
  const totals = totalCountByGroupType(items);
  const drafts = draftCountByGroupType(items);
  return GROUP_ORDER.map((groupType, index) => ({
    id: groupNodeId(groupType),
    position: { x: GROUP_X, y: index * GROUP_ROW_HEIGHT },
    data: {
      kind: "group",
      groupType,
      count: groupType === "DICTIONARY" ? dictionaryEntryCount : totals[groupType],
      draftCount: groupType === "DICTIONARY" ? 0 : drafts[groupType],
    },
  }));
}

/**
 * Two kinds of edges, forming a 3-tier Channel -> Category -> Group mindmap:
 * - channelCategory: this channel×category combination has at least one
 *   connected item, of any source.
 * - categoryGroup: this category connects to this group in at least one
 *   channel (aggregated — the specific channel is shown by the
 *   channelCategory edges instead, so this stays legible with many channels).
 */
export function buildEdges(
  channels: Channel[],
  categories: MaterialCategory[],
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
): GraphEdge[] {
  const activeChannels = channels.filter((channel) => channel.isActive);
  const leaves = leafCategories(categories);
  const edges: GraphEdge[] = [];

  for (const category of leaves) {
    const unionGroupTypes = new Set<SourceGroupType>();
    for (const channel of activeChannels) {
      for (const groupType of connectedGroupTypes(items, selections, channel.id, category.id)) {
        unionGroupTypes.add(groupType);
      }
    }
    for (const groupType of unionGroupTypes) {
      edges.push({
        id: `${categoryNodeId(category.id)}->${groupNodeId(groupType)}`,
        source: categoryNodeId(category.id),
        target: groupNodeId(groupType),
        kind: "categoryGroup",
        groupType,
        isStatic: false,
      });
    }
    // Forbidden Expressions are org-wide and unscoped, so every category is
    // always connected to the Dictionary group regardless of targeting data.
    edges.push({
      id: `${categoryNodeId(category.id)}->${groupNodeId("DICTIONARY")}`,
      source: categoryNodeId(category.id),
      target: groupNodeId("DICTIONARY"),
      kind: "categoryGroup",
      groupType: "DICTIONARY",
      isStatic: true,
    });
  }

  for (const channel of activeChannels) {
    for (const category of leaves) {
      if (connectedGroupTypes(items, selections, channel.id, category.id).length > 0) {
        edges.push({
          id: `${channelNodeId(channel.id)}->${categoryNodeId(category.id)}`,
          source: channelNodeId(channel.id),
          target: categoryNodeId(category.id),
          kind: "channelCategory",
          isStatic: false,
        });
      }
    }
  }

  return edges;
}

export function buildGraph(input: {
  channels: Channel[];
  categories: MaterialCategory[];
  items: ApplicabilityItem[];
  selections: Map<string, Set<string>>;
  dictionaryEntryCount: number;
  selectedCategoryId: string | null;
  activeChannelId: string | null;
  connectionFilter?: ConnectionFilter;
}) {
  const categoryNodes = buildCategoryNodes(
    input.categories,
    input.categories,
    input.items,
    input.selections,
    input.channels,
    input.selectedCategoryId,
    input.activeChannelId,
  );
  const visibleCategoryNodes =
    input.connectionFilter === "UNCONNECTED"
      ? categoryNodes.filter((node) => node.data.unconnectedForActive)
      : categoryNodes;
  const visibleCategoryNodeIds = new Set(visibleCategoryNodes.map((node) => node.id));
  const edges = buildEdges(input.channels, input.categories, input.items, input.selections).filter((edge) => {
    if (input.connectionFilter !== "UNCONNECTED") return true;
    if (edge.kind === "channelCategory") return visibleCategoryNodeIds.has(edge.target);
    return visibleCategoryNodeIds.has(edge.source);
  });
  // Channel and group nodes are scaffolding around categories. When the
  // filter has removed every category, leaving them on the canvas looks like
  // a graph of disconnected leftovers instead of "nothing unconnected".
  const unconnectedEmpty = input.connectionFilter === "UNCONNECTED" && visibleCategoryNodes.length === 0;
  return {
    channelNodes: unconnectedEmpty ? [] : buildChannelNodes(input.channels, input.activeChannelId),
    categoryNodes: visibleCategoryNodes,
    groupNodes: unconnectedEmpty ? [] : buildGroupNodes(input.items, input.dictionaryEntryCount),
    edges: unconnectedEmpty ? [] : edges,
  };
}
